import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs';
import {
    TwoFactorAuthSetupResponse
} from 'account-hybrid/features/authentication/models/two-factor-auth-setup-response';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
    MatDialog as MatDialog,
} from '@angular/material/dialog';

export enum TwoFactorWizardStep {
    Setup,
    Edit,
    RecoveryCodes,
    ConfirmPassword
}

/** The changes to 2FA that take the current password first. */
export enum TwoFactorWizardAction {
    NewCodes,
    NewKey,
    TurnOff,
    TurnOn
}

const ACTION_DESCRIPTIONS: Record<TwoFactorWizardAction, string> = {
    [TwoFactorWizardAction.NewCodes]: 'generate new recovery codes',
    [TwoFactorWizardAction.NewKey]: 'set up a new key',
    [TwoFactorWizardAction.TurnOff]: 'turn off two-factor authentication',
    [TwoFactorWizardAction.TurnOn]: 'turn on two-factor authentication',
};

export const TWO_FACTOR_AUTH_WIZARD_MODAL_ID = 'ftTwoFactorAuthWizard'

@Component({
    selector: 'ft-two-factor-auth-wizard',
    templateUrl: './two-factor-auth-wizard.component.html',
    styleUrls: ['./two-factor-auth-wizard.component.scss'],
    standalone: false
})
export class TwoFactorAuthWizardComponent implements OnChanges {
    @Input() twoFactorAuthSetupData: TwoFactorAuthSetupResponse;
    @Input() recoveryCodes: string[];
    @Input() step: TwoFactorWizardStep = TwoFactorWizardStep.Setup;
    stepsTypes = TwoFactorWizardStep;
    @Input() loading$: Observable<boolean>;
    @Input() error: string;
    /** False while staff act as the user: they are not asked for the user's password. */
    @Input() needsPassword = true;
    protected modalId = TWO_FACTOR_AUTH_WIZARD_MODAL_ID;

    @Output() opened = new EventEmitter<TwoFactorWizardStep>();
    /** The code, and the password confirmed for the key it is from; the code is what turns 2FA on. */
    @Output() verifyOtp = new EventEmitter<{ otp: string; currentPassword?: string }>();
    @Output() generateRecoveryCodes = new EventEmitter<string>();
    @Output() resetTwoFactorAuth = new EventEmitter<string>();
    @Output() turnOffTwoFactorAuth = new EventEmitter<string>();
    @Output() turnOnTwoFactorAuth = new EventEmitter<string>();

    otpFormGroup: FormGroup;
    passwordFormGroup: FormGroup;
    pendingAction: TwoFactorWizardAction = null;
    /** The error shown in the password step is the answer to this visit's password, never an earlier one. */
    passwordSubmitted = false;
    /** A password is on its way: Enter pressed twice must not send it twice, and count twice towards lockout. */
    private submitting = false;

    constructor(private fb: FormBuilder, private dialog: MatDialog) {
        this.otpFormGroup = this.fb.group({
            otpCode: [null, Validators.required],
        });
        this.passwordFormGroup = this.fb.group({
            currentPassword: [null, Validators.required],
        });
    }

    get pendingActionDescription(): string {
        return ACTION_DESCRIPTIONS[this.pendingAction];
    }

    // After a password, each change moves on only once it has worked; a refused password keeps the password step open.
    ngOnChanges(changes: SimpleChanges) {
        if (changes?.error?.currentValue) {
            this.submitting = false;
        }
        if (changes?.recoveryCodes?.currentValue?.length) {
            this.pendingAction = null;
            this.submitting = false;
            this.step = TwoFactorWizardStep.RecoveryCodes;
        }
        if (changes?.twoFactorAuthSetupData?.currentValue
            && (this.pendingAction === TwoFactorWizardAction.NewKey || this.pendingAction === TwoFactorWizardAction.TurnOn)) {
            this.pendingAction = null;
            this.submitting = false;
            this.step = TwoFactorWizardStep.Setup;
        }
        if (changes?.step?.currentValue === TwoFactorWizardStep.Setup && this.pendingAction === TwoFactorWizardAction.TurnOff) {
            this.pendingAction = null;
            this.closeSetupModal();
        }
    }

    onVerifyOtp() {
        if (this.otpFormGroup.valid) {
            this.verifyOtp.emit({
                otp: this.otpFormGroup.value.otpCode,
                currentPassword: this.passwordFormGroup.value.currentPassword ?? undefined,
            });
        }
    }

    closeSetupModal() {
        this.dialog.getDialogById(TWO_FACTOR_AUTH_WIZARD_MODAL_ID)?.close();
        this.otpFormGroup.reset();
        this.pendingAction = null;
        this.passwordFormGroup.reset();
    }

    closeRecoveryCodesView() {
        this.closeSetupModal();
        this.step = TwoFactorWizardStep.Edit;
    }

    onOpen() {
        // Turning 2FA on takes the password too, before a key is made
        if (this.step === TwoFactorWizardStep.Setup && this.needsPassword) {
            this.request(TwoFactorWizardAction.TurnOn);
        }
        this.opened.emit(this.step);
    }

    // Closed while asking for the password: the next opening starts again
    onClosed() {
        if (this.step === TwoFactorWizardStep.ConfirmPassword) {
            this.leaveConfirmPassword();
        }
        // The codes are shown once: closed on them, the next opening starts from the actions
        if (this.step === TwoFactorWizardStep.RecoveryCodes) {
            this.step = TwoFactorWizardStep.Edit;
        }
    }

    onGenerateRecoveryCodes() {
        this.request(TwoFactorWizardAction.NewCodes);
    }

    onResetTwoFactorAuth() {
        this.request(TwoFactorWizardAction.NewKey);
    }

    onTurnOffTwoFactorAuth() {
        this.request(TwoFactorWizardAction.TurnOff);
    }

    onConfirmPassword() {
        if (this.passwordFormGroup.valid && !this.submitting) {
            this.submitting = true;
            this.passwordSubmitted = true;
            this.emit(this.pendingAction, this.passwordFormGroup.value.currentPassword);
        }
    }

    cancelConfirmPassword() {
        // Turning on was asked for on opening, so there is nothing to go back to
        if (this.leaveConfirmPassword() === TwoFactorWizardAction.TurnOn) {
            this.closeSetupModal();
        }
    }

    private leaveConfirmPassword(): TwoFactorWizardAction {
        const action = this.pendingAction;
        this.pendingAction = null;
        this.submitting = false;
        this.passwordFormGroup.reset();
        this.step = action === TwoFactorWizardAction.TurnOn ? TwoFactorWizardStep.Setup : TwoFactorWizardStep.Edit;
        return action;
    }

    private request(action: TwoFactorWizardAction) {
        if (this.needsPassword) {
            this.pendingAction = action;
            this.passwordSubmitted = false;
            this.submitting = false;
            this.passwordFormGroup.reset();
            this.step = TwoFactorWizardStep.ConfirmPassword;
            return;
        }

        // No password to wait on: move on at once, as before.
        this.emit(action);
        switch (action) {
            case TwoFactorWizardAction.NewCodes:
                this.step = TwoFactorWizardStep.RecoveryCodes;
                break;
            case TwoFactorWizardAction.NewKey:
                this.step = TwoFactorWizardStep.Setup;
                break;
            case TwoFactorWizardAction.TurnOff:
                this.closeSetupModal();
                this.step = TwoFactorWizardStep.Setup;
                break;
        }
    }

    private emit(action: TwoFactorWizardAction, currentPassword?: string) {
        switch (action) {
            case TwoFactorWizardAction.NewCodes:
                this.generateRecoveryCodes.emit(currentPassword);
                break;
            case TwoFactorWizardAction.NewKey:
                this.resetTwoFactorAuth.emit(currentPassword);
                break;
            case TwoFactorWizardAction.TurnOff:
                this.turnOffTwoFactorAuth.emit(currentPassword);
                break;
            case TwoFactorWizardAction.TurnOn:
                this.turnOnTwoFactorAuth.emit(currentPassword);
                break;
        }
    }
}
